import { configuredApiOrigin } from '../../../../shared/config/api-origin';
import type { Candidate, ResultPage, Source, Summary } from '../../consumer/fire-signals/contract';
const PREFIX = '/read-models/normal-monitoring/fire-signals';
export class FireSignalReadError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
  }
}
function object(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function strings(value: Record<string, unknown>, keys: string[]) {
  return keys.every((k) => typeof value[k] === 'string');
}
function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}
function station(value: unknown): boolean {
  return (
    object(value) &&
    strings(value, ['stationId', 'name']) &&
    finite(value.latitude) &&
    finite(value.longitude)
  );
}
function measurement(value: unknown): boolean {
  return (
    value === null ||
    (object(value) &&
      strings(value, [
        'observationId',
        'stationId',
        'observedAtUTC',
        'rawObservedAtUTC',
        'originalUri',
        'originalHash',
        'sourceRevision',
        'rawRowHash',
        'unit',
      ]) &&
      finite(value.value) &&
      finite(value.distanceKm) &&
      finite(value.ageMinutes))
  );
}
export function validCandidate(value: unknown): value is Candidate {
  if (
    !object(value) ||
    !strings(value, ['candidateId', 'runId', 'areaRef', 'ruleHash', 'ruleVersion'])
  )
    return false;
  const s = value.satellite,
    w = value.weather,
    a = value.association;
  return (
    object(s) &&
    strings(s, [
      'detectionId',
      'provider',
      'satellite',
      'instrument',
      'productVersion',
      'acquiredAtUTC',
      'originalUri',
      'originalHash',
      'sourceRevision',
      'rawRowHash',
      'nativeConfidence',
    ]) &&
    finite(s.latitude) &&
    Math.abs(s.latitude) <= 90 &&
    finite(s.longitude) &&
    Math.abs(s.longitude) <= 180 &&
    s.crsEPSG === 4326 &&
    finite(s.rawRowNumber) &&
    ['frpMW', 'brightnessTi4Kelvin', 'brightnessTi5Kelvin', 'pixelScanKm', 'pixelTrackKm'].every(
      (k) => s[k] === null || finite(s[k]),
    ) &&
    object(w) &&
    (w.station === null || station(w.station)) &&
    object(w.metrics) &&
    measurement(w.metrics.TEMPERATURE) &&
    measurement(w.metrics.WIND_SPEED) &&
    object(a) &&
    ['WEATHER_CONTEXT_LINKED', 'NO_QUALIFIED_WEATHER', 'SATELLITE_ONLY'].includes(
      String(a.status),
    ) &&
    strings(a, ['eligibilityReason', 'context']) &&
    finite(a.distanceLimitKm) &&
    finite(a.timeLimitHours) &&
    Array.isArray(a.stationDecisions) &&
    object(value.eventReference) &&
    strings(value.eventReference, ['status', 'reason']) &&
    object(value.lifecycle)
  );
}
export function validSummary(value: unknown): value is Summary {
  return (
    object(value) &&
    ['AVAILABLE', 'EMPTY'].includes(String(value.status)) &&
    object(value.counts) &&
    ['candidates', 'linked', 'unlinked', 'eventReferences'].every((k) =>
      finite((value.counts as Record<string, unknown>)[k]),
    ) &&
    Array.isArray(value.stations) &&
    value.stations.every(station) &&
    Array.isArray(value.areas) &&
    value.areas.every(
      (a) =>
        object(a) &&
        strings(a, ['areaRef', 'name']) &&
        Array.isArray(a.bbox) &&
        a.bbox.length === 4 &&
        a.bbox.every(finite),
    )
  );
}
export function validPage<T>(value: unknown, item: (v: unknown) => v is T): value is ResultPage<T> {
  return (
    object(value) &&
    ['AVAILABLE', 'EMPTY'].includes(String(value.status)) &&
    Array.isArray(value.items) &&
    value.items.every(item) &&
    finite(value.total) &&
    finite(value.offset) &&
    finite(value.limit)
  );
}
function validSource(value: unknown): value is Source {
  return (
    object(value) &&
    strings(value, [
      'sourceId',
      'provider',
      'sourceRevision',
      'originalHash',
      'originalUri',
      'retrievedAt',
      'receivedAt',
      'sourceArchivePublishedAt',
    ])
  );
}
export function createFireSignalAdapter(fetcher: typeof fetch = fetch) {
  async function read<T>(
    path: string,
    signal: AbortSignal,
    validate: (v: unknown) => v is T,
  ): Promise<T> {
    let response: Response;
    try {
      response = await fetcher((configuredApiOrigin() ?? '') + PREFIX + path, {
        signal,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
    } catch (error) {
      if (signal.aborted) throw error;
      throw new FireSignalReadError(0, 'OFFLINE');
    }
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      throw new FireSignalReadError(response.status, 'INVALID_RESPONSE');
    }
    if (!response.ok)
      throw new FireSignalReadError(
        response.status,
        object(data) && typeof data.error === 'string' ? data.error : 'HTTP_ERROR',
      );
    if (!validate(data)) throw new FireSignalReadError(response.status, 'INVALID_RESPONSE');
    return data;
  }
  return {
    summary: (query: string, signal: AbortSignal) =>
      read('/summary' + (query ? '?' + query : ''), signal, validSummary),
    candidates: (query: string, signal: AbortSignal) =>
      read('/candidates' + (query ? '?' + query : ''), signal, (v): v is ResultPage<Candidate> =>
        validPage(v, validCandidate),
      ),
    candidate: (id: string, signal: AbortSignal) =>
      read('/candidates/' + encodeURIComponent(id), signal, validCandidate),
    sources: (signal: AbortSignal) =>
      read('/sources?limit=200', signal, (v): v is ResultPage<Source> => validPage(v, validSource)),
  };
}
